/**
 * outbox-summary.ts — pure summary of the outbox for the sync status panel.
 *
 * getPending() returns items in syncState 'pending' OR 'error' (inflight excluded),
 * so this splits them into a pending count and the list of errored items (with
 * lastError + attempts) for display + per-report visibility.
 *
 * Fase 3: `terminalItems` (syncState 'failed') are passed as a second argument
 * (from a separate `getTerminalFailed()` call) and surfaced as `terminales`.
 * They are shown distinctly in SyncStatusPanel as "necesita atención".
 */
import type { OutboxItem } from "../types.js";

export interface OutboxSummary {
  /** Items queued and not yet failed. */
  pendientes: number;
  /** Items whose last flush failed — kept for retry with backoff, shown with their reason. */
  errores: OutboxItem[];
  /**
   * Fase 3: items that exhausted MAX_ATTEMPTS and are no longer auto-retried.
   * Shown distinctly as "necesita atención" — require manual "Reintentar" to clear.
   */
  terminales: OutboxItem[];
  /** pendientes + errores.length + terminales.length — everything still waiting to sync. */
  total: number;
}

/**
 * @param items         - from getPending() (pending + backoff-eligible error items)
 * @param terminalItems - from getTerminalFailed() (failed items needing attention)
 */
export function summarizeOutbox(
  items: OutboxItem[],
  terminalItems: OutboxItem[] = [],
): OutboxSummary {
  const errores = items.filter((i) => i.syncState === "error");
  const pendientes = items.filter((i) => i.syncState === "pending").length;
  return {
    pendientes,
    errores,
    terminales: terminalItems,
    total: pendientes + errores.length + terminalItems.length,
  };
}
